import { GearSixIcon, WarningIcon } from '@phosphor-icons/react'
import classNames from 'classnames'
import { match } from 'ts-pattern'
import { DefaultButton } from '../../../components/buttons/default-button.tsx'
import { GhostButton } from '../../../components/buttons/ghost-button.tsx'
import { TooltipWithHover } from '../../../components/tooltip.tsx'
import type { Integration } from '../../../utils/api-client/api-schemas.ts'
import { IntegrationBrandIcon } from './integration-icon.tsx'
import {
  getInstanceFailureDetail,
  getInstanceHealth,
  getInstanceName,
  getIsSystemInstance,
  getProviderLabel,
  type InstanceHealth,
} from './integration-instance-utils.ts'
import * as styles from './integrations-tab.css.ts'

type InstanceCardProps = {
  integration: Integration
  onManage: () => unknown
}

export const InstanceCard: React.FC<InstanceCardProps> = ({ integration, onManage }) => {
  const health = getInstanceHealth(integration)
  const isSystem = getIsSystemInstance(integration)
  const failureDetail = getInstanceFailureDetail(integration)
  const isNeedsAttention = health === 'needs-attention'

  return (
    <div
      className={classNames(styles.instanceCard, { [styles.instanceCardUnhealthy]: isNeedsAttention })}
      data-testid="instance-card"
    >
      <div className={styles.instanceCardHeader}>
        <InstanceBranding integration={integration} className={styles.instanceCardBranding} />
        <HealthDot health={health} failureDetail={failureDetail} />
      </div>

      {isNeedsAttention && failureDetail && <p className={styles.instanceCardFailureDetail}>{failureDetail}</p>}

      <div className={styles.instanceCardFooter}>
        {isSystem ? (
          <span className={styles.systemBadge} title="Configured in the admin console">
            System
          </span>
        ) : isNeedsAttention ? (
          // Filled to draw the eye when the instance needs attention, quieter when it does not.
          <DefaultButton buttonType="secondary" size="small" onClick={onManage}>
            <span className={styles.manageButtonContent}>
              <WarningIcon size={16} weight="regular" className={styles.needsAttentionIcon} />
              Manage
            </span>
          </DefaultButton>
        ) : (
          <GhostButton
            type="secondary"
            size="small"
            onClick={onManage}
            icon={<GearSixIcon size={16} weight="regular" />}
          >
            Manage
          </GhostButton>
        )}
      </div>
    </div>
  )
}

type HealthDotProps = {
  health: InstanceHealth
  failureDetail?: string
}

export const HealthDot: React.FC<HealthDotProps> = ({ health, failureDetail }) => {
  const dotClassName = match(health)
    .with('healthy', () => styles.healthDotHealthy)
    .with('needs-attention', () => styles.healthDotNeedsAttention)
    .with('unknown', () => styles.healthDotUnknown)
    .exhaustive()
  const statusLabel = match(health)
    .with('healthy', () => 'Healthy')
    .with('needs-attention', () => 'Needs attention')
    .with('unknown', () => 'Unknown')
    .exhaustive()
  // The detail is the actionable half of the status, so it belongs in the accessible name too — a
  // screen-reader user should not have to open the drawer to learn why an instance is unhealthy.
  const label = failureDetail ? `Status: ${statusLabel}. ${failureDetail}` : `Status: ${statusLabel}`
  return (
    <TooltipWithHover
      trigger={<span className={classNames(styles.healthDot, dotClassName)} role="img" aria-label={label} />}
    >
      {label}
    </TooltipWithHover>
  )
}

type InstanceBrandingProps = {
  integration: Integration
  className?: string
}

/**
 * The icon + tool name + instance name block, shared by both cards so a tweak to it lands once. The
 * tool identifies the card (first line, matching the brand logo); the user's instance name is the
 * second-line subheader, which is what tells two instances of the same tool apart.
 */
const InstanceBranding: React.FC<InstanceBrandingProps> = ({ integration, className }) => {
  const instanceName = getInstanceName(integration)

  return (
    <div className={className}>
      <IntegrationBrandIcon integration={integration} />
      <div>
        <p className={styles.instanceCardName}>{getProviderLabel(integration.type)}</p>
        {instanceName && <p className={styles.instanceCardSubLabel}>{instanceName}</p>}
      </div>
    </div>
  )
}

type ReadOnlyInstanceCardProps = {
  integration: Integration
}

// Shown when in-product integration management is not opted in: the backend serves name + type only, so
// the card carries no health dot and no Manage/System affordances — just the tool and instance name.
export const ReadOnlyInstanceCard: React.FC<ReadOnlyInstanceCardProps> = ({ integration }) => {
  return (
    <div className={styles.readOnlyCard} data-testid="read-only-instance-card">
      <InstanceBranding integration={integration} />
    </div>
  )
}
